import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Search, 
  Upload, 
  Eye,
  Settings,
  BarChart3,
  Globe,
  Mail,
  Phone,
  MapPin,
  Clock,
  DollarSign,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ResponsiveLayout from '@/components/ResponsiveLayout';
import LoadingSpinner from '@/components/LoadingSpinner';
import companyService, { Company, CompanyStats } from '@/services/companyService';
import authService from '@/services/authService';

interface CreateCompanyForm {
  name: string;
  code: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  industry: string;
  timezone: string;
  currency: string;
}

const CompanyManagement = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  
  const [createForm, setCreateForm] = useState<CreateCompanyForm>({
    name: '',
    code: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    industry: '',
    timezone: 'Asia/Taipei',
    currency: 'TWD',
  });

  const [editForm, setEditForm] = useState<CreateCompanyForm>({
    name: '',
    code: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    industry: '',
    timezone: 'Asia/Taipei',
    currency: 'TWD',
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      handleSearch();
    } else {
      fetchCompanies();
    }
  }, [searchQuery]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const companiesData = await companyService.getAllCompanies();
      setCompanies(companiesData);
    } catch (error: any) {
      toast.error('獲取公司列表失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchCompanies();
      return;
    }

    setLoading(true);
    try {
      const results = await companyService.searchCompanies(searchQuery);
      setCompanies(results);
    } catch (error: any) {
      toast.error('搜索失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!createForm.name.trim()) {
      toast.error('請輸入公司名稱');
      return;
    }

    setLoading(true);
    try {
      await companyService.createCompany({
        ...createForm,
        is_active: true,
        created_at: new Date().toISOString(),
      });
      
      toast.success('公司創建成功！');
      setShowCreateDialog(false);
      setCreateForm({
        name: '',
        code: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        industry: '',
        timezone: 'Asia/Taipei',
        currency: 'TWD',
      });
      fetchCompanies();
    } catch (error: any) {
      toast.error('創建公司失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = async () => {
    if (!selectedCompany || !editForm.name.trim()) {
      toast.error('請輸入公司名稱');
      return;
    }

    setLoading(true);
    try {
      console.log('Updating company with data:', {
        companyId: selectedCompany.id,
        editForm: editForm
      });
      
      await companyService.updateCompany(selectedCompany.id, editForm);
      toast.success('公司更新成功！');
      setShowEditDialog(false);
      setSelectedCompany(null);
      await fetchCompanies();
    } catch (error: any) {
      console.error('Company update error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      toast.error('更新公司失敗: ' + (error.message || error.toString()));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;

    setLoading(true);
    try {
      await companyService.deleteCompany(selectedCompany.id);
      toast.success('公司已刪除');
      setShowDeleteDialog(false);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error: any) {
      toast.error('刪除公司失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (company: Company) => {
    setSelectedCompany(company);
    setEditForm({
      name: company.name,
      code: company.code || '',
      description: company.description || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
      industry: company.industry || '',
      timezone: company.timezone || 'Asia/Taipei',
      currency: company.currency || 'TWD',
    });
    setShowEditDialog(true);
  };

  const openDetailsDialog = async (company: Company) => {
    setSelectedCompany(company);
    setShowDetailsDialog(true);
    
    try {
      const stats = await companyService.getCompanyStats(company.id);
      setCompanyStats(stats);
    } catch (error: any) {
      console.error('Error fetching company stats:', error);
      setCompanyStats(null);
    }
  };

  const openDeleteDialog = (company: Company) => {
    setSelectedCompany(company);
    setShowDeleteDialog(true);
  };

  const handleLogoUpload = async (companyId: string, file: File) => {
    setLoading(true);
    try {
      await companyService.uploadCompanyLogo(companyId, file);
      toast.success('公司標誌上傳成功！');
      fetchCompanies();
    } catch (error: any) {
      toast.error('上傳失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (company.code && company.code.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <ResponsiveLayout
      title="公司管理"
      subtitle="管理系統中的所有公司"
      showBack={true}
      showBreadcrumb={true}
      headerActions={
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          創建公司
        </Button>
      }
    >

      {/* Main Content */}
      <div className="p-6">
        {/* Search Bar */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="搜索公司名稱或代碼..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleSearch}
                className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600"
              >
                搜索
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Companies Grid */}
        {loading ? (
          <LoadingSpinner size="lg" text="載入中..." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company) => (
              <Card key={company.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center overflow-hidden">
                        {company.logo_url ? (
                          <img
                            src={company.logo_url}
                            alt={company.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Building2 className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg">{company.name}</CardTitle>
                        {company.code && (
                          <Badge variant="secondary" className="text-xs">
                            {company.code}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Badge 
                      variant={company.is_active ? "default" : "outline"}
                      className={company.is_active ? "bg-green-600" : "text-red-400 border-red-400"}
                    >
                      {company.is_active ? '啟用' : '停用'}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  {company.description && (
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                      {company.description}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {company.industry && (
                        <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">
                          {company.industry}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetailsDialog(company)}
                        className="border-slate-600 text-slate-400 hover:bg-slate-700"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(company)}
                        className="border-slate-600 text-slate-400 hover:bg-slate-700"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDeleteDialog(company)}
                        className="border-red-600 text-red-400 hover:bg-red-600/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {filteredCompanies.length === 0 && !loading && (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">沒有找到公司</h3>
            <p className="text-slate-400">
              {searchQuery ? '嘗試調整搜索條件' : '還沒有任何公司，立即創建第一個吧！'}
            </p>
          </div>
        )}
      </div>

      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>創建新公司</DialogTitle>
            <DialogDescription className="text-slate-400">
              填寫以下信息來創建一個新的公司
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">公司名稱 *</Label>
              <Input
                id="create-name"
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-code">公司代碼</Label>
              <Input
                id="create-code"
                value={createForm.code}
                onChange={(e) => setCreateForm(prev => ({ ...prev, code: e.target.value }))}
                className="bg-slate-700 border-slate-600"
                placeholder="自動生成"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="create-description">描述</Label>
              <Textarea
                id="create-description"
                value={createForm.description}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-slate-700 border-slate-600"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-industry">行業</Label>
              <Select value={createForm.industry} onValueChange={(value) => setCreateForm(prev => ({ ...prev, industry: value }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="選擇行業" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="technology">科技</SelectItem>
                  <SelectItem value="manufacturing">製造業</SelectItem>
                  <SelectItem value="retail">零售</SelectItem>
                  <SelectItem value="finance">金融</SelectItem>
                  <SelectItem value="healthcare">醫療</SelectItem>
                  <SelectItem value="education">教育</SelectItem>
                  <SelectItem value="other">其他</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-currency">貨幣</Label>
              <Select value={createForm.currency} onValueChange={(value) => setCreateForm(prev => ({ ...prev, currency: value }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="TWD">TWD (台幣)</SelectItem>
                  <SelectItem value="HKD">HKD (港幣)</SelectItem>
                  <SelectItem value="USD">USD (美元)</SelectItem>
                  <SelectItem value="EUR">EUR (歐元)</SelectItem>
                  <SelectItem value="JPY">JPY (日圓)</SelectItem>
                  <SelectItem value="CNY">CNY (人民幣)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-email">電子郵件</Label>
              <Input
                id="create-email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-phone">電話</Label>
              <Input
                id="create-phone"
                value={createForm.phone}
                onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="create-website">網站</Label>
              <Input
                id="create-website"
                value={createForm.website}
                onChange={(e) => setCreateForm(prev => ({ ...prev, website: e.target.value }))}
                className="bg-slate-700 border-slate-600"
                placeholder="https://"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="create-address">地址</Label>
              <Textarea
                id="create-address"
                value={createForm.address}
                onChange={(e) => setCreateForm(prev => ({ ...prev, address: e.target.value }))}
                className="bg-slate-700 border-slate-600"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-slate-600 text-slate-400 hover:bg-slate-700"
            >
              取消
            </Button>
            <Button
              onClick={handleCreateCompany}
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              創建公司
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>編輯公司</DialogTitle>
            <DialogDescription className="text-slate-400">
              修改公司信息
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">公司名稱 *</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-code">公司代碼</Label>
              <Input
                id="edit-code"
                value={editForm.code}
                onChange={(e) => setEditForm(prev => ({ ...prev, code: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="edit-description">描述</Label>
              <Textarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-slate-700 border-slate-600"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-industry">行業</Label>
              <Select value={editForm.industry} onValueChange={(value) => setEditForm(prev => ({ ...prev, industry: value }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="選擇行業" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="technology">科技</SelectItem>
                  <SelectItem value="manufacturing">製造業</SelectItem>
                  <SelectItem value="retail">零售</SelectItem>
                  <SelectItem value="finance">金融</SelectItem>
                  <SelectItem value="healthcare">醫療</SelectItem>
                  <SelectItem value="education">教育</SelectItem>
                  <SelectItem value="other">其他</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-currency">貨幣</Label>
              <Select value={editForm.currency} onValueChange={(value) => setEditForm(prev => ({ ...prev, currency: value }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="TWD">TWD (台幣)</SelectItem>
                  <SelectItem value="HKD">HKD (港幣)</SelectItem>
                  <SelectItem value="USD">USD (美元)</SelectItem>
                  <SelectItem value="EUR">EUR (歐元)</SelectItem>
                  <SelectItem value="JPY">JPY (日圓)</SelectItem>
                  <SelectItem value="CNY">CNY (人民幣)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-email">電子郵件</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-phone">電話</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="edit-website">網站</Label>
              <Input
                id="edit-website"
                value={editForm.website}
                onChange={(e) => setEditForm(prev => ({ ...prev, website: e.target.value }))}
                className="bg-slate-700 border-slate-600"
                placeholder="https://"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="edit-address">地址</Label>
              <Textarea
                id="edit-address"
                value={editForm.address}
                onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                className="bg-slate-700 border-slate-600"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="border-slate-600 text-slate-400 hover:bg-slate-700"
            >
              取消
            </Button>
            <Button
              onClick={handleEditCompany}
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              保存更改
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>確認刪除</DialogTitle>
            <DialogDescription className="text-slate-400">
              您確定要刪除公司 "{selectedCompany?.name}" 嗎？此操作將停用該公司，但不會永久刪除數據。
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="border-slate-600 text-slate-400 hover:bg-slate-700"
            >
              取消
            </Button>
            <Button
              onClick={handleDeleteCompany}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              確認刪除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedCompany?.logo_url ? (
                <img
                  src={selectedCompany.logo_url}
                  alt={selectedCompany.name}
                  className="w-8 h-8 rounded object-cover"
                />
              ) : (
                <Building2 className="w-8 h-8 text-slate-400" />
              )}
              {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompany && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-slate-700">
                <TabsTrigger value="overview">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  概覽
                </TabsTrigger>
                <TabsTrigger value="details">
                  <Building2 className="w-4 h-4 mr-2" />
                  詳細信息
                </TabsTrigger>
                <TabsTrigger value="users">
                  <Users className="w-4 h-4 mr-2" />
                  用戶
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-slate-700 border-slate-600">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-400" />
                        <span className="text-sm text-slate-400">用戶總數</span>
                      </div>
                      <p className="text-2xl font-bold text-white">
                        {companyStats?.total_users || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-700 border-slate-600">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-slate-400">活躍用戶</span>
                      </div>
                      <p className="text-2xl font-bold text-white">
                        {companyStats?.active_users || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-700 border-slate-600">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-purple-400" />
                        <span className="text-sm text-slate-400">產品數量</span>
                      </div>
                      <p className="text-2xl font-bold text-white">
                        {companyStats?.total_products || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-700 border-slate-600">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-yellow-400" />
                        <span className="text-sm text-slate-400">庫存價值</span>
                      </div>
                      <p className="text-2xl font-bold text-white">
                        ${companyStats?.total_inventory_value || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-400">公司代碼</Label>
                      <p className="text-white">{selectedCompany.code || '未設置'}</p>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400">行業</Label>
                      <p className="text-white">{selectedCompany.industry || '未設置'}</p>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400">貨幣</Label>
                      <p className="text-white">{selectedCompany.currency || '未設置'}</p>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400">時區</Label>
                      <p className="text-white">{selectedCompany.timezone || '未設置'}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {selectedCompany.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="text-white">{selectedCompany.email}</span>
                      </div>
                    )}
                    
                    {selectedCompany.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span className="text-white">{selectedCompany.phone}</span>
                      </div>
                    )}
                    
                    {selectedCompany.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-slate-400" />
                        <a 
                          href={selectedCompany.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:underline"
                        >
                          {selectedCompany.website}
                        </a>
                      </div>
                    )}
                    
                    {selectedCompany.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 mt-1" />
                        <span className="text-white">{selectedCompany.address}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {selectedCompany.description && (
                  <div>
                    <Label className="text-slate-400">描述</Label>
                    <p className="text-white mt-1">{selectedCompany.description}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="users" className="space-y-4">
                <p className="text-slate-400">用戶管理功能將在下一個版本中實現</p>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </ResponsiveLayout>
  );
};

export default CompanyManagement;
